import {Banner, Button, CounterLabel, Stack} from '@primer/react'
import {GitCommitIcon} from '@primer/octicons-react'
import {Suspense, use, useState} from 'react'
import {ErrorBoundary} from '@github-ui/react-core/error-boundary'
import {CommentsSkeleton} from './CommentsSkeleton'
import {CommentsErrorFallback} from './CommentsErrorFallback'
import {LazyThreadsDataLoader, useShouldLoadLazyThreadData} from './LazyThreadsDataLoader'
import {
  isIssueCommentComposerEnabled,
  IssueCommentComposer,
  type ComposerAdditionalActionsContext,
  type IssueCommentComposerHandle,
} from './IssueCommentComposer'
import {useLayoutCanComment} from '../../stores/layout-selectors'
import {CommentsSort} from './use-comments-sort-state'
import {CommentsEmptyState, type CommentsEmptyStateVariant} from './CommentsEmptyState'
import type {CommentsDisplayMode} from './utils/display-mode'
import {CommentsContext} from './CommentsContext'
import {focusElement, useFocusPreviousItem} from '@github-ui/diff-lines/hooks/use-focus-previous-item'
import {isDefaultFilterState, type CommentsFilterState} from './use-comments-filter-state'
import {ViewMode} from './utils/view-mode'
import {useFilteredConversationComments} from './use-filtered-conversation-comments'
import {useFilteredReviewThreads} from './use-filtered-review-threads'
import {useReviewThreadFacts} from './use-review-thread-facts'
import {HydrateThreadPositioning} from './HydrateThreadPositioning'
import {ThreadPreview} from './ThreadPreview'
import {IssueCommentItem} from './IssueCommentItem'
import {ReviewSummaryItem} from './ReviewSummaryItem'
import {useFilteredThreadsForReviews} from './use-filtered-threads-for-reviews'
import {CommitDisclosure} from './CommitDisclosure'
import {useRouteQuery} from '@github-ui/react-core/future/use-route-query'
import {pullRequestsLayoutRoute} from '../../routes/layout-route'
import {useInterweaveCommentsWithCommits} from './use-conversation-comments-with-commits'
import {ConnectedConversationItem} from './ConnectedConversationItem'

interface CommentsProps extends Omit<ConversationCommentsProps & ReviewCommentsProps, 'isLoadingThreads'> {
  displayMode: CommentsDisplayMode
}

interface ConversationCommentsProps extends CommonCommentsProps {
  includeCommits?: boolean
  hideCommentComposer?: boolean
  /** Handle for interacting with the composer externally. */
  composerRef?: React.Ref<IssueCommentComposerHandle>
  /** Additional actions to render below the comment composer. */
  renderAdditionalComposerActions?: (context: ComposerAdditionalActionsContext) => React.ReactNode
  onSubmitted?: () => void
  handleQuoteReply: (quotedText: string) => void
  /** The login handle of the pull request author, used for issue comments rendering. */
  pullRequestAuthorLogin: string
}

interface ReviewCommentsProps extends CommonCommentsProps {
  /** The maximum number of latest comments shown when the limit is exceeded. */
  limitCount?: number
  /** Whether the number of comments exceeds the displayable limit. */
  limitExceeded?: boolean
}

interface CommonCommentsProps {
  sort: CommentsSort
  filterState: CommentsFilterState
  /**
   * Called after the user activates a thread preview's link to its comment in the diff.
   * Navigating to the comment is handled internally, so this is only for side effects the
   * consumer wants to layer on top (e.g. closing a panel that renders these comments).
   */
  onNavigateToDiffComment?: () => void
  isLoadingThreads?: boolean
}

/**
 * Renders pull request comments.
 *
 * Displays either thread previews or issue comments based on the current `viewMode`.
 * When in `thread-previews` mode and the comment limit is exceeded, a warning banner
 * is shown indicating that only the latest comments are visible.
 *
 * Content is lazily loaded with a skeleton fallback.
 */
export function Comments(props: CommentsProps) {
  const {shouldLazyLoadData, isLazyLoading: isLoadingThreads, onLazyDataLoaded} = useShouldLoadLazyThreadData()

  return (
    <>
      {shouldLazyLoadData && <LazyThreadsDataLoader onLoaded={onLazyDataLoaded} />}

      {props.displayMode === 'review-threads' ? (
        <ReviewComments {...props} isLoadingThreads={isLoadingThreads} />
      ) : (
        <ConversationComments {...props} isLoadingThreads={isLoadingThreads} />
      )}
    </>
  )
}

function ConversationComments({
  handleQuoteReply,
  onNavigateToDiffComment,
  pullRequestAuthorLogin,
  hideCommentComposer,
  sort,
  composerRef,
  renderAdditionalComposerActions,
  onSubmitted,
  filterState,
  isLoadingThreads,
  includeCommits = false,
}: ConversationCommentsProps) {
  const {viewSwitchRef} = use(CommentsContext)
  const {itemRefs, focusPrevious} = useFocusPreviousItem(() =>
    focusElement(viewSwitchRef?.current?.querySelector<HTMLButtonElement>('button[aria-pressed="true"]')),
  )
  const {
    filteredComments,
    totalConversationCommentsCount: totalLoadedCount,
    query: conversationCommentsQuery,
  } = useFilteredConversationComments({
    filterState,
    sort,
  })

  const {filteredReviewThreads, filteredReviewThreadsByReviewId, reviewsWithMissingThreads} =
    useFilteredThreadsForReviews({
      filterState,
      filteredComments,
    })

  const {commitsQuery, commentsAndCommits, totalCommitsCount, showTruncationWarning} = useInterweaveCommentsWithCommits(
    {includeCommits, filteredComments, conversationCommentsQuery, sort},
  )

  const totalCommentsCount = useRouteQuery(pullRequestsLayoutRoute, 'tabCounts')?.data?.conversationCount

  const showCommentComposer =
    isIssueCommentComposerEnabled('conversation', useLayoutCanComment()) && !hideCommentComposer

  const commentComposerAtTop = sort === CommentsSort.NewestFirst
  const commentComposerCollapsible = commentComposerAtTop

  const [commentComposerCollapsed, setCommentComposerCollapsed] = useState(true)

  const commentComposer = showCommentComposer && (
    <IssueCommentComposer
      ref={composerRef}
      collapsed={commentComposerCollapsible && commentComposerCollapsed}
      onCollapsedChange={commentComposerCollapsible ? setCommentComposerCollapsed : undefined}
      onSubmitted={onSubmitted}
      renderAdditionalActions={renderAdditionalComposerActions}
    />
  )

  const loadMoreButton = conversationCommentsQuery.hasNextPage && (
    <Stack align="center">
      <Button
        onClick={() => conversationCommentsQuery.fetchNextPage()}
        loading={conversationCommentsQuery.isFetchingNextPage}
        loadingAnnouncement="Loading previous comments"
        trailingVisual={
          totalCommentsCount ? (
            <CounterLabel style={{width: 'max-content'}}>{totalCommentsCount - totalLoadedCount}</CounterLabel>
          ) : undefined
        }
      >
        Load previous comments
      </Button>
    </Stack>
  )

  return (
    <Stack direction="vertical" gap="normal">
      {commentComposerAtTop && commentComposer}
      {includeCommits && commitsQuery.isError && (
        <CommentsErrorFallback heading="Unable to load commits" icon={GitCommitIcon} />
      )}
      {showTruncationWarning && (
        <Banner
          aria-label="Warning"
          variant="warning"
          title="Warning"
          hideTitle
          description="Only the most recent 250 commits are being shown."
        />
      )}

      {totalLoadedCount > 0 && totalLoadedCount !== filteredComments.length && (
        <p className="fgColor-muted text-small text-center m-0">
          Showing {filteredComments.length} of {totalLoadedCount}
          {conversationCommentsQuery.hasNextPage ? <> loaded</> : <></>}
          <> comments</>
        </p>
      )}

      <ErrorBoundary boundaryName="Comments" fallback={<CommentsErrorFallback />}>
        <Suspense fallback={<CommentsSkeleton count={totalCommentsCount} />}>
          <HydrateThreadPositioning threads={filteredReviewThreads} />
          {conversationCommentsQuery.isError ? (
            <CommentsErrorFallback />
          ) : conversationCommentsQuery.isPending ? (
            <CommentsSkeleton count={totalCommentsCount} />
          ) : totalLoadedCount === 0 &&
            totalCommitsCount === 0 &&
            !conversationCommentsQuery.hasNextPage &&
            // intentionally using isLoading. `isPending` is true when `enabled:false`
            (!includeCommits || !commitsQuery.isLoading) ? (
            <CommentsEmptyState />
          ) : (
            <>
              {sort === CommentsSort.OldestFirst && loadMoreButton}
              <Stack direction="vertical" gap="normal">
                {commentsAndCommits.map((item, index) => (
                  <ConnectedConversationItem key={item.key} enabled={includeCommits} type={item.type}>
                    {item.type === 'commits' ? (
                      commitsQuery.data && (
                        <CommitDisclosure commitGroups={item.commitGroups} visibleCommitOids={item.visibleCommitOids} />
                      )
                    ) : item.type === 'issue' ? (
                      <IssueCommentItem
                        comment={item}
                        pullRequestAuthorLogin={pullRequestAuthorLogin}
                        onQuoteReply={handleQuoteReply}
                        ref={itemRefs(index)}
                        onDelete={() => focusPrevious(index)}
                      />
                    ) : (
                      <ReviewSummaryItem
                        reviewSummary={item}
                        pullRequestAuthorLogin={pullRequestAuthorLogin}
                        onQuoteReply={handleQuoteReply}
                        withReviewStateIcon
                        onNavigateToDiffComment={onNavigateToDiffComment}
                        threads={filteredReviewThreadsByReviewId.get(item.id)}
                        isLoadingThreads={isLoadingThreads}
                        hasMissingReferencedThreads={reviewsWithMissingThreads.has(item.id)}
                        ref={itemRefs(index)}
                        onDelete={() => focusPrevious(index)}
                      />
                    )}
                  </ConnectedConversationItem>
                ))}
              </Stack>
              {sort === CommentsSort.NewestFirst && loadMoreButton}
            </>
          )}
        </Suspense>
      </ErrorBoundary>

      {!commentComposerAtTop && commentComposer}
    </Stack>
  )
}

/** Returns a specialized empty state only when the default filters and loaded review-thread facts support it. */
function reviewCommentsEmptyStateVariant(
  viewMode: ViewMode,
  filterState: CommentsFilterState,
  totalItemsCount: number,
  allReviewThreadsResolved: boolean | undefined,
  hasCurrentUserReviewThreads: boolean | undefined,
  limitExceeded: boolean | undefined,
): CommentsEmptyStateVariant | undefined {
  if (!isDefaultFilterState(viewMode, filterState) || limitExceeded) return undefined
  if (viewMode === ViewMode.Mine) {
    if (hasCurrentUserReviewThreads === false) return 'mine'
    return totalItemsCount === 0 ? 'review-comments' : undefined
  }
  if (viewMode === ViewMode.ThreadPreviews) {
    if (allReviewThreadsResolved) return 'all-resolved'
    return totalItemsCount === 0 ? 'review-comments' : undefined
  }
  return 'review-comments'
}

function ReviewComments({
  limitCount,
  limitExceeded,
  onNavigateToDiffComment,
  isLoadingThreads,
  filterState,
  sort,
}: ReviewCommentsProps) {
  const {
    totalThreadsCount,
    filteredThreads,
    isLoading: isLoadingFilteredThreads,
  } = useFilteredReviewThreads({
    filterState,
    sort,
  })
  const {allReviewThreadsResolved, hasCurrentUserReviewThreads} = useReviewThreadFacts()
  const {viewMode, viewSwitchRef} = use(CommentsContext)
  const {itemRefs, focusPrevious} = useFocusPreviousItem(() =>
    focusElement(viewSwitchRef?.current?.querySelector<HTMLButtonElement>('button[aria-pressed="true"]')),
  )
  const handleThreadDelete = (index: number, threadRemoved: boolean) => {
    if (threadRemoved || !itemRefs(index).current?.focus()) focusPrevious(index)
  }
  const emptyStateVariant = reviewCommentsEmptyStateVariant(
    viewMode,
    filterState,
    totalThreadsCount,
    allReviewThreadsResolved,
    hasCurrentUserReviewThreads,
    limitExceeded,
  )
  const hasFilteredItems = filteredThreads.length > 0
  const showEmptyState = !hasFilteredItems && (totalThreadsCount === 0 || emptyStateVariant !== undefined)
  const isLoading = isLoadingThreads || isLoadingFilteredThreads

  return (
    <Stack direction="vertical" gap="normal">
      {!showEmptyState && totalThreadsCount > 0 && totalThreadsCount !== filteredThreads.length && (
        <p className="fgColor-muted text-small text-center m-0">
          Showing {filteredThreads.length} of {totalThreadsCount} review comments
        </p>
      )}

      {limitExceeded && (
        <Banner
          aria-label="Warning"
          title="Warning"
          variant="warning"
          hideTitle
          description={`Only the ${limitCount} latest comments are being shown.`}
        />
      )}

      <ErrorBoundary boundaryName="Comments" fallback={<CommentsErrorFallback />}>
        <Suspense fallback={<CommentsSkeleton reviewCommentsMode />}>
          {isLoading ? (
            // Temporary mess until we can remove the suspense and error boundary. We need to coalesce _all_ the different
            // loading and error states -- threads, pages of comments, initial comments....
            <CommentsSkeleton reviewCommentsMode />
          ) : showEmptyState ? (
            <CommentsEmptyState variant={emptyStateVariant ?? 'review-comments'} />
          ) : (
            <div className="d-flex flex-column position-relative width-full gap-3">
              <HydrateThreadPositioning threads={filteredThreads} />
              {filteredThreads.map((thread, index) => (
                <ThreadPreview
                  key={thread.id}
                  ref={itemRefs(index)}
                  onDelete={(_threadId, threadRemoved) => handleThreadDelete(index, threadRemoved)}
                  thread={thread}
                  selectedReviewId={null}
                  onNavigateToDiffComment={onNavigateToDiffComment}
                  headingLevel="h2"
                />
              ))}
            </div>
          )}
        </Suspense>
      </ErrorBoundary>
    </Stack>
  )
}
